import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Send, Sparkles, ArrowRight, Loader2, AlertTriangle,
  Brain, CheckCircle2, MessageCircle, Compass, Heart, Mic, Square,
  Volume2, VolumeX,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { TrustBadge } from '../components/brand/TrustBadge';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  in_home_care: 'In-home care',
  telehealth_visit: 'Telehealth visit',
  wellness_visit: 'Wellness visit',
  companion_care: 'Companion care',
  post_discharge: 'Post-discharge care',
  chronic_care: 'Chronic care management',
  hospice_palliative: 'Hospice & palliative',
  mobile_urgent_care: 'Mobile urgent care',
  behavioral_health: 'Behavioral health',
  skilled_nursing: 'Skilled nursing',
};

const STARTERS = [
  'My mom just came home from the hospital and needs help recovering.',
  "Dad's dementia is getting harder to manage. Looking for in-home support.",
  'Looking for a hospice team for my grandfather.',
  "I'd like a wellness check for my elderly aunt.",
];

export default function AICareNavigatorPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [history, setHistory] = useState([
    {
      role: 'assistant',
      content:
        "Hi, I'm Cara, your CaraLoom care navigator. Tell me what's going on with your loved one — I'll help you figure out the right kind of care, gently and quickly.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [emergency, setEmergency] = useState(false);
  const scrollRef = useRef(null);

  // Voice-to-text state. Recording is in-memory only — no audio persisted server-side.
  // After Whisper returns we drop the transcript into the textarea so the user
  // can review/edit before sending (safer than auto-send).
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recError, setRecError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, recommendation, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput('');
    const newHist = [...history, { role: 'user', content: message }];
    setHistory(newHist);
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/care-navigator`,
        { message, session_id: sessionId, history: newHist },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.session_id) setSessionId(data.session_id);

      if (data.status === 'emergency') {
        setEmergency(true);
        setHistory((h) => [...h, { role: 'assistant', content: data.message }]);
      } else if (data.status === 'recommendation') {
        setHistory((h) => [...h, { role: 'assistant', content: data.message || "Here's what I'd recommend." }]);
        setRecommendation(data.recommendation);
      } else {
        setHistory((h) => [...h, { role: 'assistant', content: data.message }]);
      }
    } catch (e) {
      setHistory((h) => [...h, { role: 'assistant', content: "Sorry, I hit a snag. Could you try again?" }]);
    } finally {
      setLoading(false);
    }
  };

  const goToMarketplace = () => {
    if (!recommendation) return;
    const license = recommendation.license_level || '';
    const visit = recommendation.visit_type === 'in_person' ? 'in_person' : recommendation.visit_type === 'telehealth' ? 'telehealth' : '';
    const params = new URLSearchParams();
    if (license) params.set('license_type', license);
    if (visit) params.set('visit_type', visit);
    navigate(`/marketplace?${params.toString()}`);
  };

  // ─── Voice-to-text via MediaRecorder + backend Whisper ──────────────────────
  const startRecording = async () => {
    if (recording || transcribing || loading) return;
    setRecError(null);
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setRecError('Voice input is not supported in this browser. Please type instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Prefer opus/webm — Whisper accepts it directly.
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = () => transcribeRecording(rec.mimeType || 'audio/webm');
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setRecError(e?.name === 'NotAllowedError'
        ? 'Mic permission was denied. Enable microphone access for this site to use voice input.'
        : 'Could not start the microphone. Please type instead.');
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') return;
    rec.stop();
    setRecording(false);
    // Release the mic immediately — never hold the indicator longer than needed.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const transcribeRecording = async (mimeType) => {
    setTranscribing(true);
    try {
      const blob = new Blob(audioChunksRef.current, { type: mimeType });
      if (blob.size < 1000) {
        setRecError('That recording was too short — try again.');
        return;
      }
      // Base64-encode for JSON transport (same pattern as /patient/voice-search).
      const arr = new Uint8Array(await blob.arrayBuffer());
      let bin = '';
      for (let i = 0; i < arr.byteLength; i++) bin += String.fromCharCode(arr[i]);
      const b64 = btoa(bin);
      const fmt = (mimeType.split('/')[1] || 'webm').split(';')[0];
      const { data } = await axios.post(
        `${API}/care-navigator/transcribe`,
        { audio_data: b64, audio_format: fmt },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      const text = (data?.transcript || '').trim();
      if (!text) {
        setRecError('I couldn\u2019t catch that — try again or type instead.');
      } else {
        // Append to existing input so users can build a multi-clip message.
        setInput((prev) => (prev ? `${prev.trim()} ${text}` : text));
      }
    } catch (e) {
      setRecError(e?.response?.data?.detail || 'Voice transcription failed. Please type instead.');
    } finally {
      setTranscribing(false);
      audioChunksRef.current = [];
    }
  };

  // Always release the mic on unmount.
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const turnCount = history.filter((m) => m.role === 'assistant').length;
  const progress = Math.min(100, (turnCount / 5) * 100);

  return (
    <div className="min-h-screen bg-cn-bg flex flex-col" data-testid="care-navigator-page">
      {/* Top bar */}
      <header className="cn-glass border-b border-cn-border sticky top-0 z-30">
        <div className="cn-container h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <CaraLoomLogo />
            <span className="hidden md:inline-block w-px h-5 bg-cn-border" />
            <span className="hidden md:inline-flex items-center gap-1.5 text-sm font-display font-semibold text-cn-text">
              <Compass className="w-4 h-4 text-cn-primary" /> Cara · Care Navigator
            </span>
          </Link>
          <button
            onClick={() => navigate('/patient/dashboard')}
            className="cn-btn cn-btn-ghost text-sm"
            data-testid="navigator-back-button"
          >
            Back to dashboard
          </button>
        </div>
      </header>

      {/* Hero strip */}
      {!recommendation && (
        <div className="border-b border-cn-border bg-white">
          <div className="cn-container py-6 flex items-center justify-between gap-4">
            <div>
              <div className="cn-eyebrow mb-1">Cara · Care Navigator</div>
              <h1 className="font-display text-xl md:text-2xl font-bold">
                Tell me about your loved one. I&apos;ll help.
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <TrustBadge variant="encrypted" label="Private" />
              <TrustBadge variant="ai-confidence" label="GPT-5.2" />
            </div>
          </div>
          <div className="cn-container pb-3">
            <div className="h-1 bg-cn-bone-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cn-primary to-cn-secondary transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-1.5 text-[11px] uppercase tracking-wider text-cn-text-muted">
              {turnCount < 5 ? `~${5 - turnCount} more question${5 - turnCount === 1 ? '' : 's'}` : 'Almost done'}
            </div>
          </div>
        </div>
      )}

      {/* Chat */}
      <main className="flex-1">
        <div className="cn-container py-8 max-w-3xl">
          <div ref={scrollRef} className="space-y-5 mb-8 min-h-[40vh]" data-testid="navigator-chat-history">
            {history.map((m, i) => (
              <ChatBubble key={i} role={m.role} content={m.content} idx={i} />
            ))}
            {loading && (
              <div className="flex items-start gap-3" data-testid="navigator-typing">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cn-primary to-cn-sage-500 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <div className="thinking-dots inline-flex items-center gap-1 rounded-2xl bg-white border border-cn-border px-4 py-3">
                  <span className="w-1.5 h-1.5 bg-cn-primary rounded-full" />
                  <span className="w-1.5 h-1.5 bg-cn-primary rounded-full" />
                  <span className="w-1.5 h-1.5 bg-cn-primary rounded-full" />
                </div>
              </div>
            )}
            {emergency && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="navigator-emergency">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-800">This sounds urgent — please call 911 right now.</div>
                  <div className="text-sm text-red-700 mt-1">CaraLoom is a marketplace, not an emergency service. For life-threatening situations, dial 911 immediately.</div>
                </div>
              </div>
            )}
            {recommendation && (
              <RecommendationCard rec={recommendation} onGo={goToMarketplace} />
            )}
          </div>

          {/* Starter prompts (only on first turn) */}
          {history.length === 1 && !loading && (
            <div className="mb-6 flex flex-wrap gap-2" data-testid="navigator-starters">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-2 rounded-full bg-white border border-cn-border hover:bg-cn-bone-100 text-cn-text text-left max-w-md"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          {!recommendation && !emergency && (
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="cn-card p-3 flex items-end gap-2"
              data-testid="navigator-input-form"
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder={recording ? 'Listening… tap the square to stop.' : (transcribing ? 'Transcribing your voice…' : 'Type your reply, or tap the mic to speak.')}
                rows={1}
                className="flex-1 resize-none outline-none bg-transparent px-3 py-2 text-base placeholder:text-cn-text-muted"
                data-testid="navigator-input"
                disabled={loading || transcribing}
              />
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={loading || transcribing}
                className={`cn-btn !p-3 ${recording ? '!bg-red-600 !text-white hover:!bg-red-700' : 'cn-btn-ghost'}`}
                data-testid={recording ? 'navigator-mic-stop' : 'navigator-mic-start'}
                aria-label={recording ? 'Stop recording' : 'Record voice'}
                title={recording ? 'Stop recording' : 'Speak your reply'}
              >
                {transcribing
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : recording
                    ? <Square className="w-4 h-4 fill-current" />
                    : <Mic className="w-4 h-4" />}
              </button>
              <button
                type="submit"
                disabled={loading || !input.trim() || recording || transcribing}
                className="cn-btn cn-btn-primary !p-3"
                data-testid="navigator-send-button"
                aria-label="Send"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          )}

          {recError && (
            <div
              className="mt-3 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2"
              data-testid="navigator-mic-error"
              role="status"
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{recError}</span>
            </div>
          )}

          <p className="mt-5 text-[11px] text-cn-text-muted leading-relaxed">
            Your conversation is private and used only to recommend care.{' '}
            CaraLoom is a technology marketplace, not a direct care provider.{' '}
            <strong>For emergencies, dial 911.</strong>
          </p>
        </div>
      </main>
    </div>
  );
}

function ChatBubble({ role, content, idx }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`navigator-msg-${role}`}>
      <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${
        isUser ? 'bg-cn-bone-200' : 'bg-gradient-to-br from-cn-primary to-cn-sage-500'
      }`}>
        {isUser
          ? <Heart className="w-4 h-4 text-cn-primary" />
          : <Brain className="w-4 h-4 text-white" />}
      </div>
      <div className={`max-w-[78%] rounded-2xl px-4 py-3 leading-relaxed ${
        isUser
          ? 'bg-cn-primary text-white rounded-tr-md'
          : 'bg-white border border-cn-border text-cn-text rounded-tl-md'
      }`}>
        <div>{content}</div>
        {!isUser && <SpeakButton text={content} idx={idx} />}
      </div>
    </div>
  );
}

/**
 * Browser-native TTS via the Web Speech API. Zero third-party dependencies —
 * the audio is synthesised entirely on-device, so no PHI ever leaves the patient's
 * browser. Falls back to "not supported" silently if SpeechSynthesis is unavailable
 * (older Firefox on Linux, headless browsers, etc.). Only one bubble can speak at
 * a time — clicking play on a second bubble cancels the first.
 */
function SpeakButton({ text, idx }) {
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  useEffect(() => () => {
    // Cancel any in-flight utterance when this bubble unmounts.
    if (supported && utterRef.current) {
      window.speechSynthesis.cancel();
    }
  }, [supported]);

  if (!supported) return null;

  const speak = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    window.speechSynthesis.cancel(); // stop any other bubble currently speaking
    const u = new SpeechSynthesisUtterance(String(text || ''));
    u.rate = 1.0;
    u.pitch = 1.0;
    u.lang = 'en-US';
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  return (
    <button
      type="button"
      onClick={speak}
      className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-cn-text-muted hover:text-cn-primary transition-colors"
      data-testid={`navigator-speak-btn-${idx}`}
      aria-label={speaking ? 'Stop speaking' : 'Read aloud'}
      title={speaking ? 'Stop' : 'Read aloud'}
    >
      {speaking
        ? <><VolumeX className="w-3.5 h-3.5" /> Stop</>
        : <><Volume2 className="w-3.5 h-3.5" /> Listen</>}
    </button>
  );
}

function RecommendationCard({ rec, onGo }) {
  return (
    <div className="cn-card overflow-hidden mt-6" data-testid="navigator-recommendation">
      <div className="bg-gradient-to-br from-cn-primary to-cn-sage-700 text-white p-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" />
          <div className="cn-eyebrow !text-white/70">Your care recommendation</div>
        </div>
        <h3 className="font-display text-2xl font-bold leading-snug">
          {SERVICE_LABELS[rec.service_type] || rec.service_type}
          <span className="text-white/80"> · {rec.license_level}</span>
        </h3>
        {rec.rationale && (
          <p className="mt-3 text-white/90 leading-relaxed">{rec.rationale}</p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold border border-white/15">
            Urgency: {rec.urgency}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold border border-white/15">
            Visit: {rec.visit_type}
          </span>
        </div>
      </div>

      <div className="p-6">
        {Array.isArray(rec.care_plan) && rec.care_plan.length > 0 && (
          <div>
            <div className="cn-eyebrow mb-3">Suggested care plan</div>
            <ul className="space-y-2.5">
              {rec.care_plan.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-cn-primary flex-shrink-0 mt-0.5" />
                  <span className="text-cn-text">{step}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {rec.next_steps && (
          <p className="mt-5 text-sm text-cn-text-muted">{rec.next_steps}</p>
        )}
        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <button
            onClick={onGo}
            className="cn-btn cn-btn-primary flex-1"
            data-testid="navigator-go-marketplace"
          >
            Browse matched clinicians <ArrowRight className="w-4 h-4" />
          </button>
          <Link
            to="/patient/messages"
            className="cn-btn cn-btn-ghost flex-1 text-center"
            data-testid="navigator-message-team"
          >
            <MessageCircle className="w-4 h-4" /> Message care team
          </Link>
        </div>
        <div className="mt-5">
          <MarketplaceDisclaimer variant="micro" />
        </div>
      </div>
    </div>
  );
}
